import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";

export default function EmailSignupForm({
  source = "wynn_hero",
  testIdPrefix = "hero",
  successDescription,
  buttonLabel = "Join the waitlist",
}) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || loading) return;
    setLoading(true);
    try {
      const res = await api.post("/subscribe", {
        email,
        first_name: firstName || null,
        source,
      });
      const data = res.data;
      toast(data.message || "You're in.", {
        description:
          successDescription || "A quiet letter will reach you when the doors open.",
      });
      setSuccess(true);
    } catch (err) {
      const message =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof message === "string" ? message : "Couldn't subscribe.", {
        description: "Please check the email address and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const inputBase =
    "w-full bg-transparent text-[#F4F1EC] placeholder:text-[#F4F1EC]/35 border-0 border-b border-[#F4F1EC]/25 rounded-none px-0 h-12 text-base font-body outline-none transition-colors focus:border-[#FFB7C6]";

  if (success) {
    return (
      <div
        data-testid={`${testIdPrefix}-signup-success`}
        className="p-6 border border-[#F4F1EC]/20 bg-[#F4F1EC]/[0.04]"
      >
        <div className="flex items-center gap-4">
          <span className="h-9 w-9 inline-flex items-center justify-center bg-gradient-to-br from-[#FFB7C6] to-[#A8E6FF] text-[#070608] shrink-0">
            <Check size={16} strokeWidth={2.2} />
          </span>
          <div>
            <div className="font-display text-2xl text-[#F4F1EC]">
              You&apos;re <span className="italic font-light">inside</span>.
            </div>
            <div className="font-body text-sm mt-1 text-[#F4F1EC]/65">
              {successDescription || "We'll write when the first circle opens."}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      data-testid={`${testIdPrefix}-signup-form`}
      onSubmit={handleSubmit}
      className="w-full"
    >
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-5 items-end">
        <div className="sm:col-span-2">
          <label
            htmlFor={`${testIdPrefix}-first-name`}
            className="block text-[10px] uppercase tracking-[0.28em] font-body text-[#F4F1EC]/50 mb-2"
          >
            First name
          </label>
          <input
            id={`${testIdPrefix}-first-name`}
            data-testid={`${testIdPrefix}-first-name-input`}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Mira"
            className={inputBase}
            autoComplete="given-name"
          />
        </div>
        <div className="sm:col-span-3">
          <label
            htmlFor={`${testIdPrefix}-email`}
            className="block text-[10px] uppercase tracking-[0.28em] font-body text-[#F4F1EC]/50 mb-2"
          >
            Email
          </label>
          <input
            id={`${testIdPrefix}-email`}
            data-testid={`${testIdPrefix}-email-input`}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@studio.com"
            className={inputBase}
            autoComplete="email"
          />
        </div>
      </div>

      <button
        type="submit"
        data-testid={`${testIdPrefix}-submit-button`}
        disabled={loading}
        className="group mt-7 h-12 px-7 font-body text-[12px] uppercase tracking-[0.22em] inline-flex items-center gap-2 bg-[#F4F1EC] text-[#070608] hover:bg-white disabled:opacity-60 transition-colors"
      >
        {loading ? "Sending…" : buttonLabel}
        {!loading && (
          <ArrowRight
            size={16}
            strokeWidth={1.6}
            className="transition-transform group-hover:translate-x-1"
          />
        )}
      </button>
    </form>
  );
}
